import React, { useState, useRef } from 'react';
import { Loader2, Image as ImageIcon, Settings } from 'lucide-react';
import BoundingBoxEditor, { BoundingBox } from './BoundingBoxEditor';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface ImageHotspotConfig {
  image_url: string;
  image_path: string;
  image_width?: number;
  image_height?: number;
  ranking_count: number;
  bounding_boxes: BoundingBox[];
}

interface ImageHotspotEditorProps {
  config: ImageHotspotConfig;
  onChange: (config: ImageHotspotConfig) => void;
  disabled?: boolean;
}

const ImageHotspotEditor: React.FC<ImageHotspotEditorProps> = ({
  config,
  onChange,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    // Validate file
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 15 * 1024 * 1024; // 15MB

    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, GIF or WEBP');
      return;
    }

    if (file.size > maxSize) {
      setError('File exceeds 15MB limit');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/question-images/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.detail || 'Upload failed');
      }

      const data = await response.json();
      
      // Reset bounding boxes when new image is uploaded
      onChange({
        ...config,
        image_url: data.image_url,
        image_path: data.image_path,
        bounding_boxes: []
      });
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleBoundingBoxesChange = (boxes: BoundingBox[]) => {
    onChange({
      ...config,
      bounding_boxes: boxes
    });
  };

  const handleRankingCountChange = (count: number) => {
    onChange({
      ...config,
      ranking_count: Math.max(1, Math.min(30, count))
    });
  };

  const handleRemoveImage = () => {
    onChange({
      ...config,
      image_url: '',
      image_path: '',
      bounding_boxes: []
    });
  };

  const fullImageUrl = config.image_url 
    ? (config.image_url.startsWith('http') ? config.image_url : `${API_URL}${config.image_url}`)
    : '';

  return (
    <div className="space-y-4">
      {/* Configuration Section */}
      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <Settings className="w-4 h-4 text-gray-500" />
        <label className="text-sm text-gray-700">
          Number of selections required:
        </label>
        <input
          type="number"
          min={1}
          max={30}
          value={config.ranking_count}
          onChange={(e) => handleRankingCountChange(parseInt(e.target.value) || 1)}
          disabled={disabled}
          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
        />
        <span className="text-xs text-gray-500">
          (Respondents will rank their top {config.ranking_count} choice{config.ranking_count > 1 ? 's' : ''})
        </span>
      </div>

      {/* Image Upload or Editor */}
      {!config.image_url ? (
        /* Upload Zone */
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled}
          />
          
          <div
            onClick={() => !disabled && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); !disabled && setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={handleDrop}
            className={`
              h-48 w-full border-2 border-dashed rounded-lg cursor-pointer transition-all 
              flex flex-col items-center justify-center gap-3
              ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${uploading ? 'pointer-events-none' : ''}
            `}
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-10 h-10 text-gray-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Upload shelf or product image</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Drop image here or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PNG, JPG, GIF or WEBP up to 15MB
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* Bounding Box Editor */
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Draw product regions on the image
            </h4>
            <button
              onClick={handleRemoveImage}
              disabled={disabled}
              className="text-xs text-red-500 hover:text-red-600 disabled:opacity-50"
            >
              Remove Image
            </button>
          </div>
          
          <BoundingBoxEditor
            imageUrl={fullImageUrl}
            boxes={config.bounding_boxes}
            onChange={handleBoundingBoxesChange}
            disabled={disabled}
          />
          
          {config.bounding_boxes.length === 0 && (
            <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
              ⚠️ No product regions defined. Draw rectangles on the image to define clickable areas.
            </p>
          )}
          
          {config.bounding_boxes.length > 0 && config.bounding_boxes.length < config.ranking_count && (
            <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
              ⚠️ You have {config.bounding_boxes.length} region(s) but require {config.ranking_count} selection(s). 
              Consider adding more regions or reducing the selection count.
            </p>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageHotspotEditor;

